import { DIALOG_DATA, DialogRef } from "@angular/cdk/dialog";
import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, Inject, QueryList, ViewChildren } from "@angular/core";
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Application, ButtonConfig, Client, Job, Page } from "@interfaces";
import { GtagService } from "src/app/containers/gtag/gtag.service";
import { calculateButtonClasses } from "src/app/helpers/buttons";
import { ApplicationsService } from "src/app/rest/applications.service";
import { FeedbackService } from "src/app/rest/feedback.service";
import { DialogContainerComponent } from "../../dialog-container/dialog-container.component";
import { StarRatingComponent } from "../../star-rating/star-rating/star-rating.component";

export interface LeadDialogProps {
    page?: Page;
    job?: Job;
    client: Client;
    title: string;
    captureCV: boolean;
    captureJobIntent: boolean;
}

@Component({
    selector: "app-lead-dialog",
    templateUrl: "./lead-dialog.component.html",
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, DialogContainerComponent, StarRatingComponent],
})
export class LeadDialogComponent implements AfterViewInit {
    @ViewChildren(StarRatingComponent) starRating: QueryList<StarRatingComponent>;
    public dialogTitle: string;
    public captureForm: UntypedFormGroup;
    public ratingForm: UntypedFormGroup;
    public loading = false;
    public error = null;
    public submitted = false;
    public finalised = false;
    public submitting = false;
    public payload: any = {};
    public client: Client;
    public captureJobIntent = false;
    public captureCV = false;
    public page: Page;
    public job: Job;
    public applyButtonConfig: ButtonConfig = {
        textColour: "text-white",
        background: {
            type: "custom",
            value: "bg-blue-500",
        },
        textSize: "regular",
    };
	public applyButtonClasses = calculateButtonClasses(this.applyButtonConfig);

    constructor(
        public dialogRef: DialogRef<boolean>,
        private fb: UntypedFormBuilder,
        private applications: ApplicationsService,
        private feedback: FeedbackService,
        private gtag: GtagService,
        @Inject(DIALOG_DATA) data: LeadDialogProps
    ) {
        this.job = data.job;
        this.page = data.page;
        this.client = data.client;
        this.dialogTitle =
            data.title || `Sign up for updates from ${this.client.displayName || this.client.name || "us"}`;
        this.captureJobIntent = data.captureJobIntent;
        this.captureCV = data.captureCV;

        this.captureForm = this.fb.group({
            firstName: ["", Validators.required],
            lastName: ["", Validators.required],
            email: ["", [Validators.required, Validators.email]],
            phone: [""],
            location: [],
            employer: this.client._id,
            delayCV: [!data.captureCV],
            page: [this.page?._id || null],
            stage: ["register-interest"],
            relatedJobsConsent: [false],
            terms: [false, [Validators.requiredTrue]],
        });

        this.ratingForm = this.fb.group({
            rating: [3, Validators.required],
            application: [],
            job: [],
            employer: [],
        });
    }

    ngAfterViewInit() {
        this.starRating.changes.subscribe((result: QueryList<StarRatingComponent>) => {
            if (result?.first) {
                result.first.ratingUpdated.subscribe(this.handleRatingChange.bind(this));
            }
        });
    }

    public onFileUpload(event) {
        const me = this;
        const files = event.target.files || event.srcElement.files;
        const file = files[0];
        const reader: FileReader = new FileReader();

        reader.onloadend = (e) => {
            me.payload.cv = {
                data: (reader.result as any).split(",")[1],
                filename: file.name,
                type: file.type,
            };
        };
        reader.readAsDataURL(file);
    }

    public apply() {
        this.loading = true;

        this.applications
            .apply({
                ...this.payload,
                ...this.captureForm.getRawValue(),
                job: this.job,
                page: this.page,
            })
            .subscribe(
                (result: Application) => {
                    this.gtag.fireApplyEvent(this.job._id, this.job.title, result._id);
                    this.ratingForm.get("application").setValue(result._id);
                    this.ratingForm.get("employer").setValue(result.employer);

                    if (result.job) {
                        this.ratingForm.get("job").setValue(result.jobData._id);
                    }

                    this.submitted = true;
                    this.submitting = false;
                },
                (error) => {
                    this.error = true;
                }
            );
    }

    private handleRatingChange(value: number) {
        this.ratingForm.get("rating").setValue(value);
        this.submitFeedback();
    }

    private submitFeedback() {
        this.feedback.post(this.ratingForm.getRawValue()).subscribe(() => {
            this.finalised = true;
        });
    }

    public closeDialog(status = false) {
        this.dialogRef.close(status);
    }
}
